import React, { useState, useEffect, useRef, CSSProperties } from 'react';
import { API_ENDPOINTS } from '../../../config/api.config';
import { apiService } from '../../../services/apiService';
import { Minimize2, Maximize2, ExternalLink, Send, X, Plus, ArrowLeft } from 'lucide-react';
import MessageBubble from './MessageBubble';
import SessionSidebar from './SessionSidebar';
import {
    ChatMessage,
    ChatResponse,
    mapServerSessionToClient,
    SendMessageRequest,
    ChatSession
} from '../../../types/chat';
import { APIIntegrationConfig, IntegrationConfig } from '../../../types/integrations';
import '../../../../scss/ChatSessionWindow.scss';

interface ChatSessionResponse {
    sessions: ChatSession[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        hasMore: boolean;
    }
}

interface ChatSessionsProps {
    applicationId: string;
    applicationName: string;
    onClose: () => void;
    position: number;
    isMobile?: boolean;
    style?: CSSProperties;
    // Add new configurable props
    sidebarWidth?: number;
    chatWindowWidth?: number;
    chatWindowHeight?: number;
    chatWindowMargin?: number;
}

const ChatSessionWindow: React.FC<ChatSessionsProps> = ({
                                                            applicationId,
                                                            applicationName,
                                                            onClose,
                                                            position = 0,
                                                            isMobile = false,
                                                            style = {}
                                                        }) => {
    const [sessions, setSessions] = useState<ChatSession[]>([]);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [isMinimized, setIsMinimized] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const [showSidebar, setShowSidebar] = useState(false);
    const [sidebarTimer, setSidebarTimer] = useState<NodeJS.Timeout | null>(null);
    const [integrations, setIntegrations] = useState<IntegrationConfig[]>([]);
    const [hasMoreSessions, setHasMoreSessions] = useState(true);
    const [isLoadingMoreSessions, setIsLoadingMoreSessions] = useState(false);
    const [, setCurrentPage] = useState(1);
    const [isSidebarPinned, setIsSidebarPinned] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    useRef<HTMLDivElement>(null);
    const userId = localStorage.getItem('userId');
    const [localUserId, setLocalUserId] = useState(localStorage.getItem('userId'));

    // Enhanced iOS keyboard handling
    useEffect(() => {
        if (!isMobile) return;

        // Only for iOS
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        if (!isIOS) return;

        // Original focus handler
        const handleFocus = () => {
            // Scroll to the input when it gets focus
            setTimeout(() => {
                window.scrollTo(0, document.body.scrollHeight);
            }, 300);
        };

        // Add class to body when keyboard is shown
        const handleKeyboardShow = () => {
            document.body.classList.add('keyboard-visible');
            // Measure keyboard height (if possible)
            const viewportHeight = window.visualViewport?.height || window.innerHeight;
            const keyboardHeight = window.innerHeight - viewportHeight;

            if (keyboardHeight > 0) {
                document.documentElement.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
            }

            // Ensure messages end is visible
            setTimeout(() => {
                if (messagesEndRef.current) {
                    messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
                }
            }, 100);
        };

        // Remove class when keyboard is hidden
        const handleKeyboardHide = () => {
            document.body.classList.remove('keyboard-visible');
            document.documentElement.style.removeProperty('--keyboard-height');
        };

        const inputElement = document.querySelector('.mobile-chat-window input');

        if (inputElement) {
            inputElement.addEventListener('focus', handleFocus);
            inputElement.addEventListener('focus', handleKeyboardShow);
            inputElement.addEventListener('blur', handleKeyboardHide);
        }

        // Add viewport change listener for iOS
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', () => {
                if (document.activeElement?.tagName === 'INPUT') {
                    handleKeyboardShow();
                }
            });
        }

        return () => {
            if (inputElement) {
                inputElement.removeEventListener('focus', handleFocus);
                inputElement.removeEventListener('focus', handleKeyboardShow);
                inputElement.removeEventListener('blur', handleKeyboardHide);
            }

            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleKeyboardShow);
            }
        };
    }, [isMobile, messages.length]);

    const handlePinToggle = () => {
        setIsSidebarPinned(!isSidebarPinned);
        setShowSidebar(true);
    };

    const handleSidebarMouseLeave = () => {
        if (!isSidebarPinned) {
            setShowSidebar(false);
        }
    };

    useEffect(() => {
        const checkForUserIdChanges = () => {
            const currentUserId = localStorage.getItem('userId');
            if (currentUserId !== localUserId) {
                console.log('[Debug] UserId changed in localStorage, updating state');
                setLocalUserId(currentUserId);
            }
        };

        // Check every second for changes
        const interval = setInterval(checkForUserIdChanges, 1000);

        // Also listen for storage events (when localStorage changes in other tabs)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'userId') {
                console.log('[Debug] UserId changed via storage event');
                setLocalUserId(e.newValue);
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            clearInterval(interval);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [localUserId]);

    // Mouse movement effect
    useEffect(() => {
        if (!isFullscreen) return;

        const handleMouseMoveEvent = (e: MouseEvent) => {
            if (isSidebarPinned) return;

            if (e.clientX <= 40) {
                if (sidebarTimer) clearTimeout(sidebarTimer);
                setShowSidebar(true);
            } else if (e.clientX > 380 && showSidebar) {
                const timer = setTimeout(() => {
                    setShowSidebar(false);
                }, 300);
                setSidebarTimer(timer);
            }
        };

        window.addEventListener('mousemove', handleMouseMoveEvent);

        return () => {
            window.removeEventListener('mousemove', handleMouseMoveEvent);
            if (sidebarTimer) clearTimeout(sidebarTimer);
        };
    }, [isFullscreen, showSidebar, sidebarTimer, isSidebarPinned]);

    // Initialize sessions
    useEffect(() => {
        const initializeSessions = async () => {
            console.log('[Debug] Starting chat initialization with:', {
                applicationId,
                hasUserId: !!userId,
                userIdValue: userId ? `${userId.substring(0, 8)}...` : 'null' // Show partial ID for privacy
            });

            // Add this code to handle null userId
            let userIdToUse = userId || ''; // Use empty string as fallback for null
            if (!userIdToUse) {
                const tempUserId = 'temp-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
                console.log('[Debug] No userId found. Creating temporary userId:', tempUserId);
                localStorage.setItem('userId', tempUserId);
                userIdToUse = tempUserId;
                setLocalUserId(tempUserId);
            }

            if (!applicationId) {
                console.error('[Debug] Initialization aborted - missing application ID');
                setIsInitializing(false);
                return;
            }

            console.log('[Debug] Initializing sessions for app:', applicationId);

            try {
                setIsInitializing(true);

                // Get sessions from server first
                const data = await apiService.get<ChatSessionResponse>(
                    `${API_ENDPOINTS.CHAT.SESSIONS.LIST(applicationId)}?page=1&limit=10`
                );

                // Try to get sessions from localStorage
                const storageKey = `sessions_${applicationId}_${userIdToUse}`;
                const savedSessions = localStorage.getItem(storageKey);
                let localSessions: ChatSession[] = [];

                if (savedSessions) {
                    localSessions = JSON.parse(savedSessions);
                    console.log('[Debug] Found saved sessions:', {
                        count: localSessions.length,
                        sessions: localSessions.map(s => ({
                            id: s.id,
                            name: s.name,
                            firstMessage: s.firstMessage
                        }))
                    });
                }

                // Merge server sessions with local data to preserve names and messages
                const mergedSessions = data.sessions.map(serverSession => {
                    const localSession = localSessions.find(ls => ls.id === serverSession.id);
                    if (localSession) {
                        return {
                            ...serverSession,
                            name: localSession.name || serverSession.name,
                            firstMessage: localSession.firstMessage || serverSession.firstMessage
                        };
                    }
                    return serverSession;
                });

                const filteredSessions = mergedSessions
                    .filter(session => session.rag_application_id === applicationId)
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

                console.log('[Debug] Filtered sessions:', {
                    count: filteredSessions.length,
                    ids: filteredSessions.map(s => s.id)
                });

                setSessions(filteredSessions);

                // Explicitly set current session ID if we have sessions
                if (filteredSessions.length > 0) {
                    const sessionToUse = filteredSessions[0]; // Use most recent session
                    console.log('[Debug] Setting current session:', sessionToUse.id);
                    setCurrentSessionId(sessionToUse.id);
                    await loadSessionMessages(sessionToUse.id, filteredSessions);
                } else {
                    // If no sessions exist, create a new one
                    console.log('[Debug] No sessions found, creating new session');
                    await createNewSession();
                }

                await fetchIntegrations();
            } catch (error) {
                console.error('[Debug] Error initializing sessions:', error);
            } finally {
                setIsInitializing(false);
            }
        };

        initializeSessions();
    }, [applicationId, localUserId]);

    // Scroll effect
    useEffect(() => {
        if (messagesEndRef.current && !isMinimized) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages.length, isMinimized, isFullscreen]);

    // Local storage effect for messages
    useEffect(() => {
        if (!currentSessionId || !applicationId || !userId || messages.length === 0) return;

        const storageKey = `chat_${applicationId}_${userId || ''}_${currentSessionId}`;
        localStorage.setItem(storageKey, JSON.stringify(messages));
    }, [messages, currentSessionId, applicationId, userId]);

    const fetchSessions = async (page: number = 1): Promise<void> => {
        if (!applicationId) {
            console.error('[Debug] Missing required data for fetching sessions');
            return;
        }

        try {
            setIsLoadingMoreSessions(true);
            console.log('[Debug] Fetching sessions for application:', applicationId);

            const data = await apiService.get<ChatSessionResponse>(
                `${API_ENDPOINTS.CHAT.SESSIONS.LIST(applicationId)}?page=${page}&limit=10`
            );

            console.log('[Debug] Received sessions data:', {
                totalSessions: data.sessions.length,
                sessionIds: data.sessions.map(s => s.id),
                firstSessionId: data.sessions[0]?.id
            });

            const filteredSessions = data.sessions.filter(
                session => session.rag_application_id === applicationId
            );

            console.log('[Debug] Filtered sessions:', {
                totalFiltered: filteredSessions.length,
                filteredIds: filteredSessions.map(s => s.id)
            });

            if (page === 1) {
                setSessions(filteredSessions);

                if (filteredSessions.length > 0) {
                    const firstSession = filteredSessions[0];
                    console.log('[Debug] Setting current session:', firstSession.id);
                    setCurrentSessionId(firstSession.id);

                    if (!firstSession.metadata?.isNewSession) {
                        try {
                            console.log('[Debug] Attempting to load messages for session:', firstSession.id);
                            await loadSessionMessages(firstSession.id, filteredSessions);
                        } catch (error) {
                            console.error('[Debug] Error loading messages:', error);
                            setMessages([]);
                        }
                    } else {
                        console.log('[Debug] New session detected, initializing empty messages');
                        setMessages([]);
                    }
                } else {
                    console.log('[Debug] No existing sessions found, creating new session');
                    await createNewSession();
                }
            } else {
                setSessions(prev => [...prev, ...filteredSessions]);
            }

            setHasMoreSessions(data.pagination.hasMore);
            setCurrentPage(page);
        } catch (error) {
            console.error('[Debug] Error in fetchSessions:', error);
            loadLocalSessions();
        } finally {
            setIsLoadingMoreSessions(false);
        }
    };

    const loadSessionMessages = async (sessionId: string, currentSessions?: ChatSession[]): Promise<void> => {
        const currentUserId = localStorage.getItem('userId') || '';

        if (!sessionId || !applicationId) {
            console.log('[Debug] Missing required data:', { sessionId, applicationId });
            return;
        }

        console.log('[Debug] Loading messages for session:', {
            sessionId,
            applicationId
        });

        const sessionsToUse = currentSessions || sessions;
        const session = sessionsToUse.find(s => s.id === sessionId);

        if (!session) {
            console.error('[Debug] Session not found:', {
                searchedId: sessionId,
                availableSessions: sessionsToUse.map(s => s.id)
            });
            return;
        }

        // Verify this session belongs to our current application
        if (session.rag_application_id !== applicationId) {
            console.error('[Debug] Session belongs to different application', {
                sessionAppId: session.rag_application_id,
                currentAppId: applicationId
            });
            return;
        }

        try {
            // Always attempt to fetch from server first
            console.log('[Debug] Fetching messages from server');
            const data = await apiService.get<ChatResponse>(
                API_ENDPOINTS.CHAT.MESSAGES.LIST(applicationId, sessionId)
            );

            console.log('[Debug] Received messages from server:', {
                count: data.messages?.length || 0,
                firstMessage: data.messages?.[0]?.text?.substring(0, 50),
                lastMessage: data.messages?.[data.messages.length - 1]?.text?.substring(0, 50)
            });

            if (data.messages && data.messages.length > 0) {
                setMessages(data.messages);
                // Store in localStorage for backup
                const storageKey = `chat_${applicationId}_${currentUserId}_${sessionId}`;
                localStorage.setItem(storageKey, JSON.stringify(data.messages));
            } else {
                // If no messages from server, try localStorage
                const storageKey = `chat_${applicationId}_${currentUserId}_${sessionId}`;
                const savedMessages = localStorage.getItem(storageKey);
                if (savedMessages) {
                    const parsedMessages = JSON.parse(savedMessages);
                    setMessages(parsedMessages);
                } else {
                    setMessages([]);
                }
            }
        } catch (error) {
            console.error('[Debug] Failed to fetch messages:', error);
            // Try localStorage as fallback
            loadLocalMessages(sessionId);
        }
    };

    const createNewSession = async (): Promise<void> => {
        const currentUserId = localStorage.getItem('userId') || '';

        if (!applicationId || !currentUserId) {
            console.error('[Debug] Cannot create session: missing required data');

            // If missing userId, try to set one
            if (!currentUserId) {
                const tempUserId = 'temp-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
                console.log('[Debug] Setting userId before creating session:', tempUserId);
                localStorage.setItem('userId', tempUserId);
                setLocalUserId(tempUserId);
            }

            if (!applicationId) {
                return;
            }
        }

        console.log('[Debug] Creating new session for applicationId:', applicationId);

        try {
            const data = await apiService.post<ChatResponse>(
                API_ENDPOINTS.CHAT.SESSIONS.CREATE(applicationId),
                {
                    name: "New chat",
                    metadata: {
                        isNewSession: true
                    }
                }
            );

            if (data.session) {
                const mappedSession = mapServerSessionToClient(data.session);

                // Update state first
                setSessions(prev => {
                    // Check if session already exists
                    const sessionExists = prev.some(s => s.id === mappedSession.id);
                    if (sessionExists) {
                        return prev;
                    }
                    return [mappedSession, ...prev];
                });

                setCurrentSessionId(mappedSession.id);
                setMessages([]);

                // Update localStorage only after checking for duplicates
                // Use the current userId from localStorage which may have been set after component mounted
                const currentUserIdForStorage = localStorage.getItem('userId') || '';

                if (currentUserIdForStorage) {
                    const storageKey = `sessions_${applicationId}_${currentUserIdForStorage}`;
                    const currentSessions = localStorage.getItem(storageKey);
                    let sessionsToStore = [mappedSession];

                    if (currentSessions) {
                        const parsedSessions = JSON.parse(currentSessions) as ChatSession[];
                        // Filter out any existing session with the same ID
                        const uniqueSessions = parsedSessions.filter((s: ChatSession) => s.id !== mappedSession.id);
                        sessionsToStore = [mappedSession, ...uniqueSessions];
                    }

                    localStorage.setItem(storageKey, JSON.stringify(sessionsToStore));
                }
            }
        } catch (error) {
            console.error('[Debug] Error in createNewSession:', error);
            if (error instanceof Error) {
                console.error(`Failed to create new session: ${error.message}`);
            }
        }
    };

    const fetchIntegrations = async () => {
        try {
            const data = await apiService.get<{ integrations: IntegrationConfig[] }>(
                `${API_ENDPOINTS.RAG.GET}/${applicationId}`
            );
            setIntegrations(data.integrations || []);
        } catch (error) {
            console.error('[Debug] Failed to fetch integrations:', error);
        }
    };

    const updateSessionName = (sessionId: string, message: string) => {
        setSessions(prev => prev.map(s => {
            if (s.id === sessionId) {
                // Take first 50 characters or cut at last word boundary
                const nameText = message.substring(0, 50);
                const lastSpace = nameText.lastIndexOf(' ');
                const sessionName = lastSpace > 0 ? nameText.substring(0, lastSpace) : nameText;

                return {
                    ...s,
                    firstMessage: message,  // Store full message
                    name: sessionName       // Store truncated name
                };
            }
            return s;
        }));
    };

    const handleSend = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();

        // Get the current userId with fallback to empty string
        const currentUserId = localStorage.getItem('userId') || '';

        console.log('[Debug] Send attempt with state:', {
            hasInput: !!input.trim(),
            inputLength: input.trim().length,
            hasUserId: !!currentUserId,
            hasSessionId: !!currentSessionId,
            hasAppId: !!applicationId,
            appId: applicationId
        });

        if (!input.trim() || !currentUserId || !currentSessionId || !applicationId) {
            console.log('[Debug] Send prevented - missing data:', {
                hasInput: !!input.trim(),
                hasUserId: !!currentUserId,
                hasSessionId: !!currentSessionId,
                hasAppId: !!applicationId
            });

            // If there's no userId, try to set one now
            if (!currentUserId) {
                const tempUserId = 'temp-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
                console.log('[Debug] Setting userId before send:', tempUserId);
                localStorage.setItem('userId', tempUserId);
                setLocalUserId(tempUserId);
            }

            // Only return if there are other missing values
            if (!input.trim() || !currentSessionId || !applicationId) {
                return;
            }
        }

        const trimmedInput = input.trim();
        setInput('');
        setIsLoading(true);

        const currentSession = sessions.find(s => s.id === currentSessionId);

        if (!currentSession || currentSession.rag_application_id !== applicationId) {
            console.error('[Debug] Invalid session:', {
                sessionFound: !!currentSession,
                sessionAppId: currentSession?.rag_application_id,
                expectedAppId: applicationId
            });
            setIsLoading(false);
            return;
        }

        console.log('[Debug] Sending message:', {
            sessionId: currentSessionId,
            appId: applicationId,
            messageLength: trimmedInput.length,
            isFirstMessage: messages.length === 0
        });

        const newMessage: ChatMessage = {
            text: trimmedInput,
            sender: 'user',
            timestamp: new Date(),
            sessionId: currentSessionId
        };

        setMessages(prev => [...prev, newMessage]);

        try {
            const apiConfig = integrations.find(i => i.type === 'api' && i.enabled) as APIIntegrationConfig | undefined;
            const messageRequest: SendMessageRequest = {
                message: newMessage,
                history: messages
            };

            const headers: Record<string, string> = {
                'X-User-ID': currentUserId
            };

            if (apiConfig?.enabled && apiConfig?.settings.token) {
                headers['X-API-Key'] = apiConfig.settings.token;
            }

            const data = await apiService.post<ChatResponse>(
                API_ENDPOINTS.CHAT.MESSAGES.SEND(applicationId, currentSessionId),
                messageRequest,
                { headers }
            );

            setMessages(data.messages);

            // If this is the first message, update session name and metadata
            if (messages.length === 0) {
                updateSessionName(currentSessionId, trimmedInput);

                // Update metadata to remove isNewSession flag
                setSessions(prev => prev.map(s => {
                    if (s.id === currentSessionId) {
                        return {
                            ...s,
                            metadata: {
                                ...s.metadata,
                                isNewSession: false
                            }
                        };
                    }
                    return s;
                }));
            }

            // Store in localStorage
            const storageKey = `chat_${applicationId}_${currentUserId}_${currentSessionId}`;
            localStorage.setItem(storageKey, JSON.stringify(data.messages));

            // Handle messaging platform notifications
            if (data.messages.length > 0) {
                const lastMessage = data.messages[data.messages.length - 1];
                await notifyMessagingPlatforms(trimmedInput, lastMessage.text);
            }

        } catch (error) {
            console.error('[Debug] Error sending message:', error);
            const errorMessage: ChatMessage = {
                text: "Sorry, I couldn't process your message. Please try again.",
                sender: 'assistant',
                timestamp: new Date(),
                sessionId: currentSessionId
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
            scrollToBottom();
        }
    };

    const notifyMessagingPlatforms = async (query: string, response: string) => {
        const messagingIntegrations = integrations.filter(i => i.type === 'messaging' && i.enabled);
        if (!messagingIntegrations.length) return;

        try {
            await apiService.post(
                `${API_ENDPOINTS.RAG.INTEGRATIONS}/${applicationId}/notify`,
                {
                    query,
                    response,
                    integrations: messagingIntegrations
                }
            );
        } catch (error) {
            console.error('[Debug] Failed to notify messaging platforms:', error);
        }
    };

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    const handleLoadMore = async (page: number): Promise<void> => {
        if (isLoadingMoreSessions) return;
        try {
            await fetchSessions(page);
        } catch (error) {
            console.error('[Debug] Error loading more sessions:', error);
        }
    };

    const handleShowLess = () => {
        setCurrentPage(1);
        setSessions(prev => prev.slice(0, 10));
        setHasMoreSessions(true);
    };

    const loadLocalSessions = (): void => {
        const currentUserId = localStorage.getItem('userId') || '';
        const sessionsKey = `sessions_${applicationId}_${currentUserId}`;
        const savedSessions = localStorage.getItem(sessionsKey);

        if (savedSessions) {
            const parsedSessions = JSON.parse(savedSessions) as ChatSession[];
            // Filter to only include sessions for this application
            const filteredSessions = parsedSessions.filter(
                session => session.rag_application_id === applicationId
            );
            setSessions(filteredSessions);

            if (!currentSessionId && filteredSessions.length > 0) {
                setCurrentSessionId(filteredSessions[0].id);
                loadLocalMessages(filteredSessions[0].id);
            }
        }
    };

    const loadLocalMessages = (sessionId: string): void => {
        const currentUserId = localStorage.getItem('userId') || '';
        const chatKey = `chat_${applicationId}_${currentUserId}_${sessionId}`;

        console.log('[Debug] Loading local messages:', {
            key: chatKey,
            sessionId,
            applicationId
        });

        const savedMessages = localStorage.getItem(chatKey);
        console.log('[Debug] Found saved messages:', {
            hasMessages: !!savedMessages,
            messageCount: savedMessages ? JSON.parse(savedMessages).length : 0
        });

        if (savedMessages) {
            setMessages(JSON.parse(savedMessages));
        } else {
            setMessages([]);
        }
    };

    const openInNewTab = () => {
        if (currentSessionId) {
            const currentUserId = localStorage.getItem('userId') || '';
            const chatKey = `chat_${applicationId}_${currentUserId}_${currentSessionId}`;
            localStorage.setItem(chatKey, JSON.stringify(messages));
        }
        const encodedId = encodeURIComponent(applicationId);
        window.open(`/chat/${encodedId}`, '_blank');
        setIsMinimized(true);
    };

    const toggleFullscreen = () => {
        console.log('[Debug] Toggling fullscreen with sessions:', {
            currentSessions: sessions.map(s => ({
                id: s.id,
                name: s.name,
                firstMessage: s.firstMessage
            }))
        });

        // Only store sessions if they're not already in localStorage
        const currentUserId = localStorage.getItem('userId') || '';
        const storageKey = `sessions_${applicationId}_${currentUserId}`;
        const currentStorage = localStorage.getItem(storageKey);
        const currentSessions = currentStorage ? JSON.parse(currentStorage) : [];

        // Check for duplicates and only store unique sessions
        const uniqueSessions = sessions.reduce((acc, session) => {
            if (!acc.some(s => s.id === session.id)) {
                acc.push(session);
            }
            return acc;
        }, [] as ChatSession[]);

        if (JSON.stringify(currentSessions) !== JSON.stringify(uniqueSessions)) {
            localStorage.setItem(storageKey, JSON.stringify(uniqueSessions));
        }

        setIsFullscreen(!isFullscreen);
        setShowSidebar(false);
    };

    // Mobile layout
    if (isMobile) {
        return (
            <div className="mobile-chat-window" style={style}>
                {isInitializing ? (
                    <div className="loading-spinner">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="mobile-header">
                            <button
                                className="back-button"
                                onClick={onClose}
                                aria-label="Back to applications"
                            >
                                <ArrowLeft size={20} />
                                <span>Back</span>
                            </button>

                            <h2 className="header-title">
                                {applicationName}
                            </h2>

                            <div className="header-actions">
                                <button
                                    className="btn btn-light btn-sm"
                                    onClick={() => createNewSession()}
                                    title="New Chat"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Chat Body */}
                        <div className="mobile-chat-body">
                            {messages.map((msg, idx) => (
                                msg.sender === 'user' ? (
                                    <div key={idx} className="user-message-bubble">
                                        <div className="message-content">
                                            {msg.text}
                                        </div>
                                    </div>
                                ) : (
                                    <MessageBubble
                                        key={idx}
                                        isLoading={false}
                                        message={msg.text}
                                    />
                                )
                            ))}
                            {isLoading && <MessageBubble isLoading={true} message="" />}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <div className="mobile-input-area">
                            <form onSubmit={handleSend}>
                                <div className="input-container">
                                    <input
                                        type="text"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        className="form-control"
                                        placeholder="Type your message..."
                                        disabled={isLoading}
                                        autoFocus
                                    />
                                    <button
                                        type="submit"
                                        className={`send-button ${input.trim() && !isLoading ? 'active' : 'inactive'}`}
                                        disabled={!input.trim() || isLoading}
                                    >
                                        <Send size={18}/>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // Desktop layout

// Desktop layout
    if (!isMobile) {
        const defaultStyle: CSSProperties = {
            right: `${20 + (position * 420)}px`,
        };

        const finalStyle = {
            ...defaultStyle,
            ...style
        };

        return (
            <div
                className={`rag-chat-window ${isFullscreen ? 'fullscreen' : ''} ${isMinimized ? 'minimized' : ''} ${isSidebarPinned ? 'sidebar-pinned' : ''}`}
                style={finalStyle}
                data-testid="chat-window"
            >
                {isInitializing ? (
                    <div className="loading-spinner">
                    <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {isFullscreen && (
                            <div
                                className={`rag-session-sidebar ${(showSidebar || isSidebarPinned) ? 'visible' : ''} ${isSidebarPinned ? 'pinned' : ''}`}
                            >
                                <SessionSidebar
                                    sessions={sessions}
                                    currentSessionId={currentSessionId}
                                    onSessionSelect={loadSessionMessages}
                                    onNewChat={() => {
                                        createNewSession();
                                        setShowSidebar(false);
                                    }}
                                    isVisible={isSidebarPinned || showSidebar}
                                    onMouseLeave={isSidebarPinned ? undefined : handleSidebarMouseLeave}
                                    onLoadMore={handleLoadMore}
                                    hasMoreSessions={hasMoreSessions}
                                    isLoadingMore={isLoadingMoreSessions}
                                    setSessions={setSessions}
                                    setHasMoreSessions={setHasMoreSessions}
                                    onShowLess={handleShowLess}
                                    isPinned={isSidebarPinned}
                                    onPinToggle={handlePinToggle}
                                />
                            </div>
                        )}

                        <div className="desktop-chat-container">
                            {!isFullscreen && (
                                <div className="chat-header">
                                    <div className="header-content">
                                        <div className="application-title">
                                            <h6 className="mb-0 fw-semibold">{applicationName}</h6>
                                        </div>
                                        <div className="button-group">
                                            <button
                                                className="btn btn-light"
                                                onClick={() => setIsMinimized(!isMinimized)}
                                                title={isMinimized ? "Expand" : "Minimize"}
                                            >
                                                {isMinimized ? '▲' : '▼'}
                                            </button>
                                            <button
                                                className="btn btn-light"
                                                onClick={toggleFullscreen}
                                                title="Fullscreen"
                                            >
                                                <Maximize2 size={20}/>
                                            </button>
                                            <button
                                                className="btn btn-light"
                                                onClick={openInNewTab}
                                                title="Open in new tab"
                                            >
                                                <ExternalLink size={20}/>
                                            </button>
                                            <button
                                                className="btn btn-light"
                                                onClick={() => createNewSession()}
                                                title="New chat"
                                            >
                                                <Plus size={20}/>
                                            </button>
                                            <button
                                                className="btn btn-light"
                                                onClick={onClose}
                                                title="Close"
                                            >
                                                <X size={20}/>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!isMinimized && (
                                <>
                                    <div className="chat-messages-body">
                                        {isFullscreen && (
                                            <div
                                                className="fullscreen-header-action"
                                                style={{
                                                    left: isSidebarPinned ? '20%' : '20%'
                                                }}
                                            >
                                                <button
                                                    className="btn btn-light"
                                                    onClick={toggleFullscreen}
                                                    title="Exit fullscreen"
                                                >
                                                    <Minimize2 size={24}/>
                                                </button>
                                            </div>
                                        )}

                                        <div className="chat-messages-container">
                                            {messages.map((msg, idx) => (
                                                msg.sender === 'user' ? (
                                                    <div key={idx} className="user-message-bubble">
                                                        <div className="message-content">
                                                            {msg.text}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <MessageBubble
                                                        key={idx}
                                                        isLoading={false}
                                                        message={msg.text}
                                                    />
                                                )
                                            ))}
                                            {isLoading && <MessageBubble isLoading={true} message=""/>}
                                            <div ref={messagesEndRef}/>
                                        </div>
                                    </div>

                                    <div className="chat-footer">
                                        <div className="chat-input-container">
                                            <form onSubmit={handleSend} style={{ width: '100%' }}>
                                                <div className="input-wrapper">
                                                    <input
                                                        type="text"
                                                        value={input}
                                                        onChange={(e) => setInput(e.target.value)}
                                                        placeholder="Type your message..."
                                                        disabled={isLoading}
                                                        autoFocus
                                                    />
                                                    <div className="send-button-wrapper">
                                                        <button
                                                            type="submit"
                                                            className="btn btn-link"
                                                            disabled={!input.trim() || isLoading}
                                                        >
                                                            <Send
                                                                size={18}
                                                                className={`send-icon ${input.trim() && !isLoading ? 'active' : 'inactive'}`}
                                                            />
                                                        </button>
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    }
};

export default ChatSessionWindow;